/**
 * Publishing Dashboard — validation gate before Publish becomes available.
 * Route: /kcis/admin/university/course-builder/:id/publishing (or embedded)
 *
 * Renders the output of `GET /kcis/admin/kape/courses/{id}/publishing-check`
 * as a live checklist. Publish button unlocks only when publish_ready=true.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { KCIS_ADMIN_PALETTE as PAL } from "./KcisAdminShell";
import PreviewAsStudentButton from "./PreviewAsStudentButton";

const STATUS_STYLE = {
  ok: { bg: "#10B981", label: "✓" },
  warn: { bg: "#F59E0B", label: "!" },
  fail: { bg: "#EF4444", label: "✗" },
};

export default function PublishingPanel({ course, onPublished }) {
  const [check, setCheck] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [podcasting, setPodcasting] = useState(false);
  const [podcast, setPodcast] = useState(course.podcast || null);
  const [quality, setQuality] = useState(course.quality_report || null);
  const [runningQuality, setRunningQuality] = useState(false);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get(`/kcis/admin/kape/courses/${course.id}/publishing-check`);
      setCheck(data);
    } catch {
      setCheck({ publish_ready: false, checks: [], summary: {} });
    }
    try {
      const { data: q } = await api.get(`/kcis/admin/kape/courses/${course.id}/quality-review`);
      setQuality(q);
    } catch { /* ignore — fresh course has no report */ }
  }, [course.id]);

  const runQuality = async () => {
    setRunningQuality(true);
    try {
      const { data } = await api.post(`/kcis/admin/kape/courses/${course.id}/quality-review`);
      setQuality(data);
      toast.success(`Quality review · ${data.summary}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Quality review failed");
    } finally { setRunningQuality(false); }
  };

  useEffect(() => { reload(); }, [reload]);

  const publish = async () => {
    setPublishing(true);
    try {
      await api.post(`/kcis/admin/kape/courses/${course.id}/publish`);
      toast.success("Published to LMS");
      onPublished?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally { setPublishing(false); }
  };

  const compilePodcast = async () => {
    setPodcasting(true);
    try {
      const { data } = await api.post(`/kcis/admin/kape/courses/${course.id}/podcast/compile`);
      setPodcast(data.podcast);
      toast.success("Podcast compiled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Podcast compile failed");
    } finally { setPodcasting(false); }
  };

  return (
    <div className="space-y-6" data-testid="publishing-panel">
      <div className="rounded-2xl p-5 border" style={{ borderColor: PAL.border, background: PAL.panel }}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest" style={{ color: PAL.muted }}>
              Publishing checklist
            </div>
            <h3 className="text-lg font-semibold mt-1">
              {check?.publish_ready ? "Ready to publish" : "Not ready to publish"}
            </h3>
            {check?.summary && (
              <div className="text-xs mt-1" style={{ color: PAL.muted }}>
                {check.summary.ok} ok · {check.summary.warn} warn · {check.summary.fail} fail
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <PreviewAsStudentButton courseId={course.id} testIdSuffix="publish" />
            <button onClick={publish}
                    disabled={!check?.publish_ready || publishing}
                    className="rounded-full px-5 py-2 text-sm font-medium disabled:opacity-40"
                    style={{ background: check?.publish_ready ? PAL.accent : "#4B5563",
                              color: "#0F0B14" }}
                    data-testid="publishing-publish-btn">
              {publishing ? "Publishing…" : "Publish to LMS"}
            </button>
            <button onClick={reload} className="text-xs" style={{ color: PAL.accent }}
                    data-testid="publishing-refresh">
              Refresh checklist
            </button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
          {(check?.checks || []).map((c, i) => {
            const s = STATUS_STYLE[c.status] || STATUS_STYLE.warn;
            return (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg"
                   style={{ background: "#0F0B14", border: `1px solid ${PAL.border}` }}
                   data-testid={`publishing-check-${c.name.replace(/\s+/g,'-').toLowerCase()}`}>
                <span className="w-6 h-6 rounded-full inline-flex items-center justify-center text-xs font-bold"
                      style={{ background: s.bg, color: "white" }}>{s.label}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{c.name}</div>
                  {c.hint && <div className="text-xs mt-0.5" style={{ color: PAL.muted }}>{c.hint}</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* KAPE 2.0 — AI Quality Review */}
      <div className="rounded-2xl p-5 border" style={{ borderColor: PAL.border, background: PAL.panel }}
           data-testid="publishing-quality-review">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest" style={{ color: PAL.muted }}>
              KAPE 2.0 · AI Quality Review
            </div>
            <h3 className="text-lg font-semibold mt-1">
              {quality?.summary || "Not yet reviewed"}
            </h3>
            {quality?.generated_at && (
              <div className="text-xs mt-1" style={{ color: PAL.muted }}>
                {quality.publish_ready ? "Ready to publish" : "Blockers present"} · reviewed {quality.generated_at.slice(0, 10)}
              </div>
            )}
          </div>
          <button onClick={runQuality} disabled={runningQuality}
                  className="rounded-full px-4 py-2 text-sm font-medium disabled:opacity-40"
                  style={{ background: PAL.accent, color: "#0F0B14" }}
                  data-testid="quality-review-run">
            {runningQuality ? "Reviewing…" : (quality ? "Re-run review" : "Run quality review")}
          </button>
        </div>
        {(quality?.checks || []).length > 0 && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
            {quality.checks.map((c, i) => {
              const s = STATUS_STYLE[c.status] || STATUS_STYLE.warn;
              return (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg"
                     style={{ background: "#0F0B14", border: `1px solid ${PAL.border}` }}
                     data-testid={`quality-check-${c.name.replace(/\s+/g,'-').toLowerCase()}`}>
                  <span className="w-6 h-6 rounded-full inline-flex items-center justify-center text-xs font-bold"
                        style={{ background: s.bg, color: "white" }}>{s.label}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{c.name}</div>
                    {c.message && <div className="text-xs mt-0.5" style={{ color: PAL.muted }}>{c.message}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Podcast generator */}
      <div className="rounded-2xl p-5 border" style={{ borderColor: PAL.border, background: PAL.panel }}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest" style={{ color: PAL.muted }}>
              Podcast Generator
            </div>
            <h3 className="text-lg font-semibold mt-1">
              {podcast ? podcast.episode_title : "No podcast yet"}
            </h3>
            {podcast && (
              <div className="text-xs mt-1" style={{ color: PAL.muted }}>
                {podcast.chapter_markers?.length || 0} chapters · {Math.round((podcast.total_duration_seconds || 0) / 60)} min · voice {podcast.voice_id?.slice(0, 8)}
              </div>
            )}
          </div>
          <button onClick={compilePodcast} disabled={podcasting}
                  className="rounded-full px-4 py-2 text-sm font-medium"
                  style={{ background: PAL.accent, color: "#0F0B14" }}
                  data-testid="podcast-compile-btn">
            {podcasting ? "Compiling…" : (podcast ? "Recompile podcast" : "Compile podcast")}
          </button>
        </div>
        {podcast && (
          <div className="mt-4 space-y-3">
            <audio src={podcast.mp3_url} controls className="w-full" data-testid="podcast-audio-player" />
            <div className="text-xs whitespace-pre-wrap" style={{ color: PAL.muted }}>
              {podcast.show_notes}
            </div>
            <details>
              <summary className="text-xs cursor-pointer" style={{ color: PAL.accent }}>
                Chapter markers ({podcast.chapter_markers?.length || 0})
              </summary>
              <ol className="text-xs mt-2 list-decimal pl-4">
                {(podcast.chapter_markers || []).map((c, i) => (
                  <li key={i}>{c.title} @ {Math.round(c.start_seconds)}s ({Math.round(c.duration_seconds)}s)</li>
                ))}
              </ol>
            </details>
          </div>
        )}
      </div>
    </div>
  );
}
